/**
 * Hook to fetch and cache the paragraph ID map for a negotiation version.
 *
 * Used to look up stable para_id values from paragraph indices,
 * enabling cross-platform comment anchoring between Word and web.
 */

import { useQuery } from '@tanstack/react-query'
import { getVersionParagraphs, type ParagraphMapEntry } from '../services/negotiationsApi'

export function useParagraphMap(negotiationId: number, versionNumber: number) {
  const query = useQuery({
    queryKey: ['paragraphMap', negotiationId, versionNumber],
    queryFn: () => getVersionParagraphs(negotiationId, versionNumber),
    enabled: negotiationId > 0 && versionNumber > 0,
    staleTime: Infinity, // Paragraph map doesn't change for a given version
    gcTime: 30 * 60 * 1000, // Keep in cache 30 min
  })

  /** Look up para_id by paragraph index */
  function getParaId(paragraphIndex: number | undefined): string | undefined {
    if (paragraphIndex === undefined || paragraphIndex === null) return undefined
    const map = query.data?.paragraphs
    if (!map) return undefined
    const entry = map.find((p: ParagraphMapEntry) => p.index === paragraphIndex)
    return entry?.para_id
  }

  return {
    paragraphMap: query.data?.paragraphs ?? [],
    isLoading: query.isLoading,
    getParaId,
  }
}
